import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { DataTable, type ColumnDef } from '@/shared/components/ui/DataTable';
import { EmptyPanel, ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import type { PlatformUsageLiveSession } from '../types';
import { formatOptionalText, formatTenantLabel, formatUserLabel } from '../utils/displayLabels';
import { useSortedSessions } from './PlatformUsageOverview';

interface ActiveSessionsTableProps {
  sessions: PlatformUsageLiveSession[];
  locale: string;
  isLoading: boolean;
  isError: boolean;
}

export function ActiveSessionsTable({
  sessions,
  locale,
  isLoading,
  isError,
}: ActiveSessionsTableProps) {
  const { t } = useTranslation();
  const sortedSessions = useSortedSessions(sessions);
  const unknown = t('platformUsage.common.unknown');

  const columns = useMemo((): ColumnDef<PlatformUsageLiveSession>[] => {
    return [
      {
        key: 'tenant',
        header: t('platformUsage.sessions.columns.tenant'),
        accessor: (row) =>
          row.tenantName ?? formatTenantLabel(row.tenantExternalId, row.tenantId, unknown),
        sortValue: (row) =>
          row.tenantName ?? formatTenantLabel(row.tenantExternalId, row.tenantId, unknown),
      },
      {
        key: 'user',
        header: t('platformUsage.sessions.columns.user'),
        accessor: (row) => (
          <div className="space-y-1">
            <div className="font-medium">
              {row.userName ?? formatUserLabel(row.userExternalId, row.userId, unknown)}
            </div>
            <div className="text-xs text-muted-foreground">{row.userEmail ?? unknown}</div>
            <div className="text-xs text-muted-foreground">{row.userPhone ?? unknown}</div>
          </div>
        ),
        sortValue: (row) =>
          row.userName ?? row.userEmail ?? formatUserLabel(row.userExternalId, row.userId, unknown),
      },
      {
        key: 'status',
        header: 'Status',
        accessor: () => (
          <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
            Online
          </span>
        ),
        sortValue: () => 'online',
      },
      {
        key: 'product',
        header: t('platformUsage.sessions.columns.product'),
        accessor: (row) => row.productSlug,
        sortValue: (row) => row.productSlug,
      },
      {
        key: 'module',
        header: t('platformUsage.sessions.columns.module'),
        accessor: (row) => formatOptionalText(row.module, unknown),
        sortValue: (row) => formatOptionalText(row.module, unknown),
      },
      {
        key: 'page',
        header: t('platformUsage.sessions.columns.page'),
        accessor: (row) => (
          <span className="font-mono text-xs">{formatOptionalText(row.pagePath, unknown)}</span>
        ),
        sortValue: (row) => formatOptionalText(row.pagePath, unknown),
      },
      {
        key: 'device',
        header: t('platformUsage.sessions.columns.device'),
        accessor: (row) => formatOptionalText(row.deviceType, unknown),
        sortValue: (row) => formatOptionalText(row.deviceType, unknown),
      },
      {
        key: 'lastActivity',
        header: t('platformUsage.sessions.columns.lastActivity'),
        accessor: (row) => (
          <time dateTime={row.lastOccurredAt} className="tabular-nums text-xs">
            {new Date(row.lastOccurredAt).toLocaleString(locale)}
          </time>
        ),
        sortValue: (row) => new Date(row.lastOccurredAt).getTime(),
      },
    ];
  }, [locale, t, unknown]);

  return (
    <Card className="min-w-0">
      <CardHeader>
        <CardTitle className="text-base">{t('platformUsage.sessions.title')}</CardTitle>
      </CardHeader>
      <CardContent className="p-0 sm:p-0">
        {isLoading ? (
          <LoadingPanel className="py-16" />
        ) : isError ? (
          <div className="p-6">
            <ErrorPanel message={t('platformUsage.sessions.loadError')} />
          </div>
        ) : sortedSessions.length === 0 ? (
          <div className="p-6">
            <EmptyPanel
              title={t('platformUsage.sessions.emptyTitle')}
              message={t('platformUsage.sessions.emptyMessage')}
            />
          </div>
        ) : (
          <DataTable
            data={sortedSessions}
            columns={columns}
            rowKey={(row) => row.sessionId}
            initialPageSize={10}
            emptyMessage={t('platformUsage.sessions.emptyTitle')}
          />
        )}
      </CardContent>
    </Card>
  );
}
